/**
 * Camada 1 · grafico de barras INTERATIVO.
 *
 * NENHUM GRAFICO DESTE SISTEMA E FIGURA. O numero que aponta um problema precisa LEVAR
 * ate ele: grafico que so informa obriga a pessoa a sair procurando na mao o que ele
 * acabou de apontar. O minimo obrigatorio, e onde cada item esta implementado abaixo:
 *
 *   tooltip no hover ........... <title> por barra, mais o painel de leitura ao lado
 *   clique que filtra .......... aoSelecionar, e clicar de novo desfaz
 *   legenda clicavel ........... aoAlternarSerie liga e desliga a serie
 *   estado do filtro visivel ... faixa "mostrando X de Y" e botao de limpar, sempre
 *   recortes combinaveis ....... selecao e um Set, entao dois filtros somam
 *   sem dado ................... o eixo aparece vazio e DIZ por que, sem serie inventada
 *
 * DESENHADO EM SVG E NAO EM CANVAS de proposito: cada barra e um elemento real, com foco,
 * com rotulo e com evento de teclado. Em canvas, nada disso existe para quem nao usa mouse,
 * e um grafico que so responde a hover e um grafico morto no celular.
 */

import { t } from '@complexity/i18n';
import { useId, useMemo, useState, type KeyboardEvent } from 'react';

/**
 * O rotulo legivel de uma serie, para a LEGENDA.
 *
 * ---------------------------------------------------------------------------
 * O DEFEITO QUE ELA CONSERTA, medido em 06/09/2026, 22:07
 * ---------------------------------------------------------------------------
 * A legenda desenhava `d.serie` CRU, e `serie` e um PAPEL DE COR dos tokens (`erro`,
 * `alerta`, `sucesso`, `info`, `neutro`). Na tela aparecia literalmente:
 *
 *     neutro   alerta   info   sucesso
 *
 * ou seja, vocabulario de codigo exposto a quem usa o sistema, NOS DOIS IDIOMAS. Quem visse
 * "neutro" ao lado de "alerta" leria como categoria de negocio, e nao ha categoria nenhuma
 * ali: sao nomes de cor.
 *
 * ---------------------------------------------------------------------------
 * POR QUE UM MAPA AQUI, e nao um campo novo em `BarraDado`
 * ---------------------------------------------------------------------------
 * Um campo `rotuloDaSerie` no contrato obrigaria TODO grafico de TODO produto a preenche-lo,
 * e quem esquecesse voltaria a mostrar o nome cru. O mapa conserta todos de uma vez, sem
 * mudar contrato e sem exigir nada de ninguem.
 *
 * PAPEL DESCONHECIDO CAI NO PROPRIO NOME, e nao numa palavra inventada: um produto que use
 * `serie` para outra coisa (o nome de uma aeronave, uma unidade) continua vendo o que
 * escreveu. Errar para o literal e o lado seguro; errar para um rotulo traduzido seria
 * renomear o dado de alguem.
 */
const ROTULO_DA_SERIE: Readonly<Record<string, string>> = {
  erro: 'Crítico',
  alerta: 'Atenção',
  sucesso: 'Concluído',
  info: 'Em andamento',
  neutro: 'Aguardando',
  marca: 'Destaque',
  ia: 'Sugestão de IA',
};

function rotuloDaSerie(serie: string): string {
  const conhecido = ROTULO_DA_SERIE[serie];
  return conhecido ? t(conhecido) : serie;
}

export interface BarraDado {
  /** Chave estavel, usada na selecao. Nao pode ser o indice: ao reordenar, o filtro
   *  passaria a valer para outra barra sem ninguem perceber. */
  readonly chave: string;
  readonly rotulo: string;
  readonly valor: number;
  /** Papel de cor entre os tokens. Sem isso, cada grafico escolheria o proprio hex. */
  readonly serie?: string;
}

export interface GraficoBarrasProps {
  readonly titulo: string;
  readonly dados: readonly BarraDado[];
  /** Por que nao ha dado. Obrigatorio: o grafico vazio DIZ o motivo, e nao some da tela. */
  readonly vazio: string;
  /** Chaves selecionadas agora. Vem de fora para dois graficos compartilharem o filtro. */
  readonly selecionadas?: ReadonlySet<string>;
  /** Clique numa barra. Ausente, as barras nao viram botao: nada finge ser clicavel. */
  readonly aoSelecionar?: (chave: string) => void;
  readonly aoLimpar?: () => void;
  /**
   * O NOME DO BOTÃO DE LIMPAR, quando a tela precisa dizer o que ele limpa. Nasceu em 11/09/2026
   * para o extrato de IA, onde ele limpa todos os filtros, inclusive o período. Ausente, continua
   * "limpar filtro".
   */
  readonly rotuloLimpar?: string;
  /** Series desligadas pela legenda. */
  readonly seriesOcultas?: ReadonlySet<string>;
  readonly aoAlternarSerie?: (serie: string) => void;
  /** Como escrever o valor. Fica de fora do componente porque moeda, porcentagem e
   *  contagem se leem de formas diferentes e o grafico nao tem como adivinhar qual e. */
  readonly formatarValor?: (valor: number) => string;
}

export function GraficoBarras({
  titulo,
  dados,
  vazio,
  selecionadas,
  aoSelecionar,
  aoLimpar,
  rotuloLimpar,
  seriesOcultas,
  aoAlternarSerie,
  formatarValor = (v) => String(v),
}: GraficoBarrasProps) {
  // A barra sob o cursor ou sob o foco do teclado. Os dois caminhos alimentam o mesmo
  // estado, entao a leitura ao lado funciona igual para mouse e para teclado.
  const [emDestaque, setEmDestaque] = useState<string | null>(null);

  // O ID DO TITULO E UNICO POR GRAFICO, e ate 16/09/2026 era fixo (`cx-gb-titulo`). Com dois
  // graficos na mesma tela, o `aria-labelledby` do segundo apontava para o titulo do primeiro,
  // e o leitor de tela anunciava o grafico errado. Os Relatorios montam varios por visao.
  // A MARCA `data-cx-grafico` e o que as suites usam para achar o grafico, no lugar do id, que
  // agora muda a cada montagem.
  const idTitulo = useId();

  const visiveis = useMemo(
    () => dados.filter((d) => !d.serie || !seriesOcultas?.has(d.serie)),
    [dados, seriesOcultas],
  );

  // O maior valor define a escala. `|| 1` evita divisao por zero quando todos sao zero,
  // caso em que as barras aparecem rentes ao eixo, que e a leitura correta.
  const maximo = useMemo(() => Math.max(...visiveis.map((d) => d.valor), 0) || 1, [visiveis]);

  const series = useMemo(
    () => [...new Set(dados.map((d) => d.serie).filter((s): s is string => Boolean(s)))],
    [dados],
  );

  const temFiltro = (selecionadas?.size ?? 0) > 0 || (seriesOcultas?.size ?? 0) > 0;

  // Sem dado: o eixo aparece e a tela DIZ por que. Nao ha serie inventada e nao ha
  // grafico sumindo, que faria a pessoa achar que perdeu registro.
  if (dados.length === 0) {
    return (
      <section className="cx-cartao p-cx-4" aria-labelledby={idTitulo} data-cx-grafico="">
        <h3 id={idTitulo} className="mb-cx-3 text-cx-sm font-semibold text-texto">
          {titulo}
        </h3>
        <div className="cx-vazio" role="status">
          {vazio}
        </div>
      </section>
    );
  }

  /**
   * ENTER E ESPACO ACIONAM O ALVO.
   *
   * O TIPO E `HTMLDivElement` desde 09/09/2026, e nao `SVGRectElement`: o alvo de clique
   * deixou de ser a barra desenhada e passou a ser a COLUNA inteira. Um `div` com
   * `role="button"` NAO recebe o acionamento do teclado de graca, diferente de um `<button>`
   * de verdade, entao sem esta funcao a coluna seria alcancavel por Tab e inerte ao ser
   * acionada, que e pior que nao ser alcancavel.
   */
  const aoTeclar = (e: KeyboardEvent<HTMLDivElement>, chave: string) => {
    if (!aoSelecionar) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      aoSelecionar(chave);
    }
  };

  return (
    <section className="cx-cartao p-cx-4" aria-labelledby={idTitulo} data-cx-grafico="">
      <header className="mb-cx-3 flex flex-wrap items-center justify-between gap-cx-2">
        <h3 id={idTitulo} className="text-cx-sm font-semibold text-texto">
          {titulo}
        </h3>

        {/* Estado do filtro SEMPRE visivel. Sem esta faixa, quem chega na tela ja filtrada
            acha que os registros sumiram, e vai procurar o defeito no lugar errado. */}
        {/*
          AS DUAS FRASES PASSAM POR `t()`, e ate 06/09/2026 nao passavam.
          ----------------------------------------------------------------------------
          Este arquivo NAO IMPORTAVA `t` em linha nenhuma, e o efeito alcanca a PLATAFORMA
          INTEIRA: todo grafico de todo produto escrevia "mostrando 6 de 6" e "limpar filtro"
          em portugues, com a tela em ingles. Medido no `medico-concierge`, mas o defeito
          nunca foi dele.

          A frase e QUEBRADA EM DUAS CHAVES porque os numeros entram no meio dela. Uma chave
          unica com os valores dentro seria uma chave diferente a cada contagem, e nenhuma
          delas casaria no dicionario.
        */}
        <p className="text-cx-xs text-texto-suave" role="status">
          {/*
            O CONTADOR REFLETE A SELECAO, e antes ele nao refletia.
            Medido na tela em 09/09/2026: com o Pix selecionado (`aria-pressed=true`), a
            faixa dizia "mostrando 5 de 5". `visiveis` conta barra NAO OCULTA pela legenda,
            que e outro eixo de filtro: a selecao nao mexe nele. O fundador leu "5 de 5" com
            o filtro ativo e chamou de bug, e era.
            AS DUAS CONTAGENS CONTINUAM EXISTINDO porque medem coisas diferentes: com serie
            oculta, o denominador continua sendo o total de barras.
          */}
          {t('mostrando')} {(selecionadas?.size ?? 0) > 0 ? selecionadas?.size : visiveis.length}{' '}
          {t('de')} {dados.length}
          {temFiltro && aoLimpar && (
            <button
              type="button"
              onClick={aoLimpar}
              className="ml-cx-2 min-h-toque text-marca underline"
            >
              {rotuloLimpar ?? t('limpar filtro')}
            </button>
          )}
        </p>
      </header>

      {/* Legenda clicavel: liga e desliga a serie. So aparece quando ha mais de uma,
          porque legenda de item unico e ruido. */}
      {series.length > 1 && (
        <ul className="mb-cx-3 flex flex-wrap gap-cx-2">
          {series.map((s) => {
            const oculta = seriesOcultas?.has(s) ?? false;
            return (
              <li key={s}>
                <button
                  type="button"
                  onClick={() => aoAlternarSerie?.(s)}
                  aria-pressed={!oculta}
                  className={[
                    'min-h-toque rounded-cx-full border px-cx-3 text-cx-xs',
                    oculta
                      ? 'border-borda text-texto-fraco line-through'
                      // A LEGENDA SEGUE O MESMO MATERIAL DAS BARRAS: tinta com alpha, e nao
                      // fundo cheio. Ela fica logo acima do grafico, e dois materiais
                      // diferentes para o mesmo dado leem como duas coisas diferentes.
                      : 'border-marca/50 bg-marca/15 text-marca backdrop-blur-vidro',
                  ].join(' ')}
                >
                  {rotuloDaSerie(s)}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/*
        A ALTURA DAS COLUNAS E DEFINIDA AQUI, e a razao esta no defeito de 07/09/2026.
        Com `items-end`, cada coluna encolhia ate o conteudo, entao o `height: 100%` do SVG
        nao tinha contra o que resolver e virava `auto`. Um SVG com `viewBox="0 0 10 100"` e
        altura automatica adota a razao do proprio viewBox, que e 1 para 10: numa coluna de
        355px a barra passava a ter 3.550px e cobria a tela inteira.
        `items-stretch` da a cada coluna a altura da linha, e ai a porcentagem resolve.
      */}
      <div className="flex items-stretch gap-cx-1" style={{ height: '12rem' }}>
        {visiveis.map((d) => {
          const selecionada = selecionadas?.has(d.chave) ?? false;
          const alturaPorcento = (d.valor / maximo) * 100;

          return (
            /*
             * ============================================================
             * A COLUNA INTEIRA E O ALVO, e nao so a barra desenhada
             * ============================================================
             * Pedido dele em 09/09/2026, olhando o Financeiro: "TODA A AREA ACIMA DO NOME TEM
             * QUE SER CLICAVEL. ESTA DIFICIL CLICAR NO CARTAO COM APENAS 1 REGISTRO. NO
             * MOBILE PIORA."
             *
             * O DEFEITO ERA GEOMETRICO E NAO DE CODIGO: o alvo era o `<rect>`, cuja ALTURA E
             * O PROPRIO VALOR. Na captura dele o Pix somava R$ 60.704,52 e o Cartao de
             * credito R$ 900: a barra do cartao tinha DOIS PIXELS de altura. Clicar nela com
             * o dedo e impossivel, e e justamente a barra pequena que a pessoa quer
             * investigar, porque a grande ela ja entendeu.
             *
             * E QUANTO MENOS DADO, PIOR: com um registro so, uma barra fica cheia e todas as
             * outras ficam rentes ao eixo. O grafico fica clicavel exatamente onde nao
             * interessa.
             *
             * A COLUNA TEM A ALTURA DA LINHA (12rem) e a largura dividida igualmente, entao o
             * alvo passa a ter centenas de pixels em qualquer valor, inclusive ZERO. E o
             * mesmo raciocinio do alvo de toque de 44px aplicado a um grafico: quem decide o
             * tamanho do alvo e o DEDO, e nao o dado.
             */
            <div
              key={d.chave}
              className={[
                'flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-cx-1',
                aoSelecionar
                  ? 'cursor-pointer rounded-cx-md hover:bg-superficie-afundada focus-visible:outline focus-visible:outline-2 focus-visible:outline-marca'
                  : '',
              ].join(' ')}
              tabIndex={aoSelecionar ? 0 : undefined}
              role={aoSelecionar ? 'button' : undefined}
              aria-pressed={aoSelecionar ? selecionada : undefined}
              aria-label={aoSelecionar ? `${d.rotulo}: ${formatarValor(d.valor)}` : undefined}
              onClick={aoSelecionar ? () => aoSelecionar(d.chave) : undefined}
              onKeyDown={aoSelecionar ? (e) => aoTeclar(e, d.chave) : undefined}
              onPointerEnter={() => setEmDestaque(d.chave)}
              onPointerLeave={() => setEmDestaque(null)}
            >
              {/* Cada barra e um SVG proprio, e nao um retangulo dentro de um SVG unico:
                  assim ela recebe foco, rotulo e evento por conta propria, e o teclado
                  percorre as barras na ordem em que elas aparecem. */}
              {/*
                  `flex-1` E `min-h-0` NO LUGAR DE `height: 100%`: a altura passa a ser o que
                  sobra da coluna depois do rotulo, calculada pelo flex, e nao uma
                  porcentagem que depende de o pai ter altura. `min-h-0` e obrigatorio: sem
                  ele, o tamanho minimo de um item flex e o conteudo dele, e o SVG voltaria
                  a empurrar a coluna para fora do contorno.
              */}
              {/*
                O SVG NAO SE ANUNCIA QUANDO O PAI JA SE ANUNCIA, e esta e a correcao de
                13/09/2026.

                MEDIDO NA TELA: cada barra tinha DOIS elementos com o mesmo nome acessivel, o
                `<div role="button">` de fora e este `<svg role="img">` de dentro, os dois
                com "Agendado: 2". Quem usa leitor de tela ouvia CADA barra duas vezes, e um
                grafico de quatro barras virava oito anuncios identicos em pares.

                A ESCOLHA DE QUAL FICA E DO PAI, e nao deste: e o pai que recebe o clique e o
                foco, entao e ele que precisa ter nome. O desenho aqui dentro e o retrato
                daquele numero, e retrato de dado ja anunciado e decoracao.

                SEM `aoSelecionar` O PAI NAO SE ANUNCIA (o `aria-label` dele fica indefinido,
                linha 286), e ai este SVG volta a ser a unica voz da barra: por isso a
                condicao, e nao um `aria-hidden` fixo. Fixo, o grafico nao interativo ficaria
                MUDO para quem nao ve.
              */}
              <svg
                className="w-full min-h-0 flex-1"
                role={aoSelecionar ? undefined : 'img'}
                aria-hidden={aoSelecionar ? true : undefined}
                aria-label={aoSelecionar ? undefined : `${d.rotulo}: ${formatarValor(d.valor)}`}
                preserveAspectRatio="none"
                viewBox="0 0 10 100"
              >
                <rect
                  x="0"
                  // O SVG cresce de cima para baixo, entao a barra comeca onde ela termina.
                  y={100 - alturaPorcento}
                  width="10"
                  height={alturaPorcento}
                  rx="1"
                  // A cor sai dos tokens via currentColor, herdado da classe do elemento pai.
                  fill="currentColor"
                  /*
                   * A BARRA E TINTA, e nao bloco de cor. Padrao da plataforma desde
                   * 09/09/2026, por pedido dele: "O GRAFICO NO CLARO E ESCURO ESTAO MUITO
                   * FORTE COMO O BOTAO.... TEM QUE ARRUMAR TUDO" e "TUDO GLASS E SUAVE".
                   *
                   * `fill-opacity` E NAO `backdrop-filter`: desfoque de fundo NAO funciona
                   * dentro de SVG, entao o vidro aqui e a propria opacidade do preenchimento.
                   * O efeito percebido e o mesmo, porque a barra assenta sobre o cartao e a
                   * cor passa a tingir em vez de cobrir.
                   *
                   * A SELECIONADA CONTINUA SE DISTINGUINDO, e por DOIS sinais somados: mais
                   * tinta e o contorno. Um so nao basta, e a regra dura do grafico interativo
                   * exige que o estado do filtro seja visivel: sem isso a pessoa clica, a
                   * tela filtra, e o grafico nao diz qual barra ela escolheu.
                   *
                   * O `stroke` E DA COR CHEIA de proposito: e ele que devolve a definicao que
                   * o alpha tirou, e e o que impede a barra de virar uma mancha.
                   */
                  className={[
                    'text-marca transition-[fill-opacity]',
                    selecionada
                      ? 'fill-opacity-80 [fill-opacity:0.8]'
                      : 'fill-opacity-30 [fill-opacity:0.3]',
                    aoSelecionar ? 'cursor-pointer' : '',
                    emDestaque === d.chave ? '[fill-opacity:0.55]' : '',
                  ].join(' ')}
                  stroke="currentColor"
                  strokeWidth={selecionada ? 0.35 : 0.2}
                  strokeOpacity={selecionada ? 0.9 : 0.55}
                  /*
                   * O ALVO E OS EVENTOS SUBIRAM PARA A COLUNA, e nao foram apagados.
                   *
                   * Eram sete atributos aqui, e virar dois `onPointer*` no elemento pai:
                   * `pointer*` cobre mouse, dedo e caneta com um caminho so, e era por isso
                   * que existiam um `onMouseEnter` E um `onTouchStart` fazendo o mesmo.
                   *
                   * DOIS ALVOS CONCENTRICOS seriam pior que um alvo pequeno: o teclado
                   * pararia duas vezes na mesma barra, o leitor de tela anunciaria cada uma
                   * em dobro, e sair do `rect` para a area vazia da propria coluna apagaria
                   * o destaque com o ponteiro ainda dentro do alvo.
                   */
                  // Equivalente por TOQUE do hover. Sem isto, a leitura de valor so
                  // existiria para quem usa mouse, e o grafico ficaria morto no celular.
                >
                  {/* O <title> dentro do <rect> e o tooltip nativo do SVG: funciona sem
                      biblioteca, sem posicionamento manual e sem sair da tela na borda. */}
                  <title>{`${d.rotulo}: ${formatarValor(d.valor)}`}</title>
                </rect>
              </svg>

              <span className="w-full truncate text-center text-cx-xs text-texto-suave">
                {d.rotulo}
              </span>
            </div>
          );
        })}
      </div>

      {/* Leitura do valor em destaque, abaixo do grafico. E o que substitui o tooltip
          flutuante no celular, onde nao existe hover e o <title> nativo nao aparece. */}
      <p className="mt-cx-2 min-h-[1.25rem] text-cx-xs text-texto" role="status">
        {emDestaque
          ? (() => {
              const d = visiveis.find((x) => x.chave === emDestaque);
              return d ? `${d.rotulo}: ${formatarValor(d.valor)}` : '';
            })()
          : ''}
      </p>
    </section>
  );
}
